import React, { useEffect, useState } from "react";

import { useNotify } from "react-admin";

import { Box, Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import TextField, { TextFieldProps } from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";

import { CustomerFileTag } from "types/customer-file-tag";
import { CustomerFileDetailed } from "types/customer-files";

import Button from "components/atoms/Button";
import ColorChip from "components/atoms/ColorChip";
import { H3 } from "components/atoms/Typography";
import CustomAutocompleteInput from "components/molecules/CustomAutocompleteInput";

type CustomerFileTagsProps = {
  fileTags: CustomerFileTag[];
  loadingCustomerFileTags: boolean;
  isTagEditionEnabled: boolean;
  customerFile: CustomerFileDetailed;
  handleSaveTags: (tagIds: number[]) => void;
};

export default function CustomerFileTags({
  fileTags,
  loadingCustomerFileTags,
  isTagEditionEnabled,
  customerFile,
  handleSaveTags,
}: CustomerFileTagsProps) {
  const defaultTags = fileTags.filter((tag) => customerFile.customer_file_tags.includes(tag.id));
  const [isEditingTags, setIsEditingTags] = useState(false);
  const getTagsFromIds = (tagIds: number[], tags: CustomerFileTag[]) =>
    tagIds.map((tagId) => tags.find((tag) => tag.id === tagId)).filter((tag): tag is CustomerFileTag => !!tag);

  const [actualTags, setActualTags] = useState<CustomerFileTag[]>(
    getTagsFromIds(customerFile.customer_file_tags, fileTags)
  );

  useEffect(() => {
    setActualTags(getTagsFromIds(customerFile.customer_file_tags, fileTags));
  }, [customerFile, fileTags]);

  const notify = useNotify();
  const saveTags = () => {
    try {
      handleSaveTags(actualTags.map((tag) => tag.id));
    } catch (error) {
      notify("Erreur lors de la sauvegarde des tags", { type: "error" });
      return;
    }
    setIsEditingTags(false);
  };

  const handleTagsChange = (newValue: CustomerFileTag[]) => {
    setActualTags(newValue);
  };

  const customRenderedInput = (params: JSX.IntrinsicAttributes & TextFieldProps) => {
    return <TextField fullWidth autoFocus {...params} />;
  };

  interface CustomOption {
    text: string;
    color: string;
    [key: string]: any;
  }

  const closeDialog = () => {
    setIsEditingTags(false);
    setActualTags(defaultTags);
  };

  const customGetOptionLabel = (option: CustomOption): string => option.text || "";

  const customRenderTags = (option: CustomOption): JSX.Element => (
    <Box
      onClick={() => {
        setActualTags((prev) => prev.filter((tag) => tag.id !== option.id));
      }}
    >
      <ColorChip label={option.text} hexColor={option.color} />
    </Box>
  );
  const customRenderOption = (option: CustomOption) => <ColorChip label={option.text} hexColor={option.color} />;

  if (loadingCustomerFileTags) {
    return <Box m={2}>Chargement des tags...</Box>;
  }
  return (
    <Box m={2}>
      {!isEditingTags && (
        <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="flex-start">
          {actualTags.map((tag) => (
            <ColorChip label={tag.text} hexColor={tag.color} />
          ))}
          {isTagEditionEnabled && (
            <Button version="invisible" onClick={() => setIsEditingTags(true)}>
              <EditIcon />
              Éditer les tags
            </Button>
          )}
        </Box>
      )}
      <Dialog fullWidth open={isEditingTags} maxWidth="md">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <H3 textAlign="center">Edition de tags</H3>
            <Box display="flex" justifyContent="flex-end">
              <Button version="invisible" onClick={closeDialog}>
                <CloseIcon />
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box m={5} textAlign="center">
            <Box mb={3}>
              <CustomAutocompleteInput
                // Only displaying options not already selected
                options={fileTags.filter(function (obj) {
                  return actualTags.indexOf(obj) === -1;
                })}
                values={actualTags}
                customGetOptionLabel={customGetOptionLabel}
                customRenderedInput={customRenderedInput}
                customOnChangeFunction={handleTagsChange}
                customRenderOption={customRenderOption}
                customRenderTags={customRenderTags}
              />
            </Box>
            <Box justifyContent={"space-between"} display="flex" flexWrap="wrap">
              <Button onClick={closeDialog} version="ghost">
                Annuler
              </Button>
              <Button
                onClick={() => {
                  saveTags();
                }}
                version="fancy"
              >
                Sauvegarder
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
